import { Link } from "react-router-dom";

import { Badge, Panel } from "../components/ui";
import type { AppConfig } from "../config";
import { Button } from "../f7";
import { useResource } from "../hooks/useResource";
import { PRODUCTS, type ProductDef } from "./registry";

/**
 * What this console covers, per product — the strip that opens the overview.
 *
 * The single most misleading thing a two-product console can do is look
 * complete while serving one product. The figures below are labelled by
 * product — Labeeb's businesses and MCP's ledger are different counts — and
 * this strip says which of those products the gateway can actually reach.
 *
 * So this is not a status widget. It is the scope statement for every number
 * below it, and it belongs above them rather than in a settings page nobody
 * opens during an incident.
 */
export function ProductCoverage({ config }: { config: AppConfig }) {
  return (
    <Panel className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 bg-slate-950/[2.5%] px-4 py-2 dark:bg-white/5">
        <h2 className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
          Coverage
        </h2>
        <Button plain href="/governance/permissions">
          Products &amp; health
        </Button>
      </div>
      <div className="divide-y divide-slate-950/5 dark:divide-white/10">
        {PRODUCTS.map((p) => (
          <Row key={p.id} product={p} config={config} />
        ))}
      </div>
    </Panel>
  );
}

function Row({ product, config }: { product: ProductDef; config: AppConfig }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5">
      <span className="w-16 shrink-0 text-sm font-medium text-slate-950 dark:text-white">
        {product.label}
      </span>
      {product.declared ? (
        <DeclaredStatus product={product} config={config} />
      ) : (
        <>
          <Badge tone="amber">Not wired</Badge>
          {/* The blurb only. `needs` is two lines of SSM path and a filename —
              true, actionable, and not what belongs on the page an operator
              opens to see where the fleet stands. It is one click away on
              Products & health, and on the title here. */}
          <span
            className="min-w-0 flex-1 truncate text-xs text-slate-500 dark:text-slate-400"
            title={`Not wired — needs ${product.needs}`}
          >
            {product.blurb}
          </span>
        </>
      )}
    </div>
  );
}

/**
 * A declared product still may not be mounted HERE — MCP in prod is exactly
 * that. The probe is the only thing that can tell them apart, and the two
 * answers call for different actions, so they get different words.
 */
function DeclaredStatus({ product, config }: { product: ProductDef; config: AppConfig }) {
  const { state } = useResource<{ ok: boolean }>(config, `${product.apiPrefix}/admin/health`);

  if (state.kind === "loading") {
    return <Badge tone="neutral">Checking…</Badge>;
  }
  if (state.kind === "ready") {
    return (
      <>
        <Badge tone="green">Live</Badge>
        <span className="min-w-0 flex-1 truncate text-xs text-slate-500 dark:text-slate-400">
          {product.blurb}
        </span>
      </>
    );
  }
  if (state.kind === "notfound") {
    return (
      <>
        <Badge tone="amber">Not mounted here</Badge>
        <span className="min-w-0 flex-1 text-xs text-slate-500 dark:text-slate-400">
          The gateway mounted no route for it in this environment — its{" "}
          <span className="ref">lambda_arn</span> parameter is absent.
        </span>
      </>
    );
  }
  // Health needs no token, so a refusal is about the request and not the
  // product. Saying "unreachable" would send someone hunting a downed Lambda.
  if (state.kind === "signedout" || state.kind === "denied") {
    return (
      <>
        <Badge tone="neutral">Not checked</Badge>
        <span className="min-w-0 flex-1 text-xs text-slate-500 dark:text-slate-400">
          The probe did not run. It says nothing about the product —{" "}
          <Link to="/governance/permissions" className="underline">
            details
          </Link>
          .
        </span>
      </>
    );
  }
  return (
    <>
      <Badge tone="red">Unreachable</Badge>
      <span className="min-w-0 flex-1 text-xs text-slate-500 dark:text-slate-400">
        Health did not answer. Every figure from this product is stale or missing.
      </span>
    </>
  );
}
